import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { odooProductTemplateUrl, odooUrl } from '@/lib/utils/odoo-url'
import { useFulfillmentRisk } from '../api/queries'
import type { RiskItem, RiskLevel, RiskSection } from '../api/types'
import { RiskCell } from '../components/RiskCell'
import { isCriticalRisk } from '../components/risk'

type Section = 'commercial' | 'samples'
type RiskFilter = 'all' | RiskLevel

export function FulfillmentRiskPage() {
  const { data, isLoading, isFetching, isError, error, refetch } = useFulfillmentRisk()
  const [section, setSection] = useState<Section>('commercial')
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const sectionData: RiskSection | undefined =
    section === 'commercial' ? data?.commercial : data?.samples
  const items = useMemo<RiskItem[]>(() => sectionData?.items ?? [], [sectionData])
  const summary = sectionData?.summary

  const filtered = useMemo<RiskItem[]>(() => {
    const needle = debounced.trim().toLowerCase()
    return items.filter((r) => {
      if (riskFilter !== 'all' && r.risk_level !== riskFilter) return false
      if (!needle) return true
      // Sprint 09 Item 5 / D-5: null-safe string accesses. Same bug
      // class as the 2026-04-24 Margins crash — backend can return
      // null on any of these fields for partial / orphan rows.
      const haystack = [
        r.order_number,
        r.customer,
        r.product,
        r.reason,
      ]
        .map((s) => (s ?? '').toLowerCase())
        .join(' ')
      return haystack.includes(needle)
    })
  }, [items, riskFilter, debounced])

  const columns = useMemo<ColumnDef<RiskItem, unknown>[]>(
    () => [
      {
        id: 'risk_level',
        header: 'Risk',
        cell: (ctx) => <RiskCell level={ctx.row.original.risk_level} />,
      },
      {
        id: 'order_number',
        header: 'Order',
        accessorKey: 'order_number',
        // Sprint 09 Item 5 — render '—' when null instead of letting
        // TanStack render "null" / undefined-as-empty. Same for the
        // other nullable string columns below.
        cell: (ctx) => (
          <a
            href={odooUrl('sale.order', ctx.row.original.order_id)}
            target="_blank"
            rel="noreferrer"
            className="text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {ctx.row.original.order_number ?? '—'}
          </a>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        accessorKey: 'customer',
        cell: (ctx) => (ctx.getValue() as string | null) ?? '—',
      },
      {
        id: 'scheduled_date',
        header: 'Scheduled',
        accessorKey: 'scheduled_date',
        cell: (ctx) => (ctx.getValue() as string | null) ?? '—',
      },
      {
        id: 'product',
        header: 'Product',
        cell: (ctx) => {
          const tmplId = ctx.row.original.product_tmpl_id
          const label = ctx.row.original.product ?? '—'
          if (tmplId == null) return label
          return (
            <a
              href={odooProductTemplateUrl(tmplId)}
              target="_blank"
              rel="noreferrer"
              className="text-link hover:underline"
              title="Open product in Odoo (click 'Forecasted' for the stock-forecast report)"
              onClick={(e) => e.stopPropagation()}
            >
              {label}
            </a>
          )
        },
      },
      {
        id: 'needed',
        header: 'Needed',
        accessorKey: 'needed',
        cell: (ctx) => {
          const v = ctx.getValue() as number | undefined
          return v == null ? '—' : v.toLocaleString()
        },
      },
      {
        id: 'available_for_pick',
        header: 'Available',
        accessorKey: 'available_for_pick',
        cell: (ctx) => {
          const v = ctx.getValue() as number | undefined
          return v == null ? '—' : v.toLocaleString()
        },
      },
      {
        id: 'shortfall',
        header: 'Shortfall',
        accessorKey: 'shortfall',
        cell: (ctx) => {
          const v = ctx.getValue() as number | undefined
          if (v == null) return '—'
          return (
            <span className={v > 0 ? 'text-danger-text' : undefined}>
              {v > 0 ? v.toLocaleString() : '—'}
            </span>
          )
        },
      },
      {
        id: 'reason',
        header: 'Reason',
        accessorKey: 'reason',
        cell: (ctx) => {
          const v = (ctx.getValue() as string | null) ?? '—'
          return (
            <span
              className="block max-w-[360px] truncate text-sm text-secondary"
              title={v}
            >
              {v}
            </span>
          )
        },
      },
    ],
    [],
  )

  // `/` focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Fulfillment Risk"
        description="Stock-gap risk per scheduled pick. Critical rows (stock shortage + ship within 30d) accent red."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Total" value={(summary?.total ?? 0).toLocaleString()} />
          <MetricCard
            variant="inline"
            label="Critical"
            value={(summary?.CRITICAL ?? 0).toLocaleString()}
            valueTone={(summary?.CRITICAL ?? 0) > 0 ? 'danger' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Action req"
            value={(summary?.ACTION_REQUIRED ?? 0).toLocaleString()}
            valueTone={(summary?.ACTION_REQUIRED ?? 0) > 0 ? 'warning' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="At risk"
            value={(summary?.AT_RISK ?? 0).toLocaleString()}
            valueTone={(summary?.AT_RISK ?? 0) > 0 ? 'warning' : 'default'}
          />
          <MetricCard variant="inline" label="OK" value={(summary?.OK ?? 0).toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search order, customer, product, reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="Section"
              value={section}
              onChange={setSection}
              options={[
                { value: 'commercial', label: 'Commercial' },
                { value: 'samples', label: 'Samples' },
              ]}
            />
            <ToolbarDivider />
            <Select
              aria-label="Risk level"
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value as RiskFilter)}
            >
              <option value="all">All risk levels</option>
              <option value="CRITICAL">Critical</option>
              <option value="ACTION_REQUIRED">Action required</option>
              <option value="AT_RISK">At risk</option>
              <option value="WATCH">Watch</option>
              <option value="OK">OK</option>
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {filtered.length.toLocaleString()} of {items.length.toLocaleString()}
            </ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
          <DataTable
            error={isError ? error : undefined}
            onRetry={() => void refetch()}
            errorSubject="fulfillment risk"
            data={filtered}
            columns={columns}
            loading={isLoading}
            criticalRowPredicate={isCriticalRisk}
            getRowId={(r) => `${r.pick_number}-${r.product_id}`}
            moduleId="fulfillment-risk"
            onRowClick={(r) =>
              window.open(odooUrl('sale.order', r.order_id), '_blank')
            }
            maxHeight="100%"
            columnWidths={{
              risk_level: 100,
              order_number: 120,
              scheduled_date: 110,
              needed: 90,
              available_for_pick: 100,
              shortfall: 90,
            }}
            minTableWidth={1100}
            emptyState={
              <EmptyState
                variant="no-match"
                title="No risks match"
                description="Try clearing the risk-level filter or the search."
              />
            }
          />
        </div>
      </div>
    </div>
  )
}
